import type { AxiosResponse } from 'axios';
import { DependencyFilter, DependencySortField } from './dependencyTypes';
import {
  Api as GeneratedApi,
  GetAssetDependenciesByIdParams as GetAssetDependenciesByIdParameters,
  GetAssetDependenciesResponse,
  HttpClient,
  RequestParams as RequestParameters,
  SortOrder,
  CreatePartFinderReportRequest,
  CreatePartFinderReportResponse,
  ListPartFinderReportsResponse,
  FailureDiagnosticsResponse,
  GetPartFinderReportResponse,
  UpdatePartFinderReportRecommendationRequest,
  UpdatePartFinderReportRecommendationResponse,
  RetryPartFinderReportResponse,
  CancelPartFinderReportResponse,
  UpdatePartFinderReportDescriptionRequest,
  UpdatePartFinderReportDescriptionResponse,
  CreatePartFinderBatchRequest,
  CreatePartFinderBatchResponse,
  ListPartFinderBatchesResponse,
  GetPartFinderBatchResponse,
  UpdatePartFinderBatchNameRequest,
  UpdatePartFinderBatchNameResponse,
  ListPartFinderActivityResponse,
  GetFolderFiltersResponse,
  GetReportGroupFiltersResponse,
  HasSubfolderDescriptionsResponse,
  FolderColumnVisibilityResponse,
  SystemHealthResponse,
  ListNsnCandidatesResponse,
  ContentType,
  PartImageIdentificationResponse,
  CreateReportFromPartImageCandidateRequest,
  CreateReportFromPartImageCandidateResponse,
  NarrowPartImageCandidatesRequest,
  NarrowPartImageCandidatesResponse,
  PartImageCharacteristicQuestionsResponse,
  PartImageItemTypeOption,
  PartImageItemTypesResponse,
  MergePartImageItemsRequest,
  CropPartImageItemRequest,
  CreateBatchFromPartImageItemsRequest,
  CreateBatchFromPartImageItemsResponse,
  GetPartFinderReportRelationshipsResponse,
} from './generatedApi';

export type GetAssetDependenciesByIdExtendedParameters = Omit<
  GetAssetDependenciesByIdParameters,
  'filter' | 'sortBy' | 'sortOrder'
> & {
  filter?: DependencyFilter;
  sortBy?: DependencySortField;
  sortOrder?: SortOrder;
};
import { FeatureFlagName } from '@/utils/featureFlags';

export type PartImageNarrowTarget = Pick<NarrowPartImageCandidatesRequest, 'itemIndex' | 'wholePhoto'>;

export interface PartImageSearchRequest {
  clientRequestId: string;
  images: File[];
  context?: string;
  autoSplit?: boolean;
}

export interface ViewerFileResponse {
  data: ArrayBuffer;
}

export interface GetMetadataFieldsWithDataResponse {
  metadataFieldNames: string[];
}

export interface LabelOption {
  name: string;
  depth: number;
  count: number;
}

export interface FolderLabelOptionsResponse {
  labels: LabelOption[];
}

type GeneratedAssetsType = GeneratedApi<unknown>['assets'];

interface EnhancedAssetsType extends Omit<GeneratedAssetsType, 'getAssetDependenciesById'> {
  getAssetDependenciesById(
    query: GetAssetDependenciesByIdExtendedParameters,
    parameters?: RequestParameters
  ): Promise<AxiosResponse<GetAssetDependenciesResponse>>;
  getSupplementalAssets(
    tenantId: string,
    assetId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<{ supplementalAssets: unknown[]; pageData: unknown }>>;
  deleteSupplementalAssets(
    tenantId: string,
    assetId: string,
    data: { supplementalAssetIds: string[] }
  ): Promise<AxiosResponse<void>>;
  putCriticalGeometry(
    tenantId: string,
    assetId: string,
    data: { releaseUnitVector?: [number, number, number] }
  ): Promise<AxiosResponse<void>>;
  putManifestCriticalGeometry(tenantId: string, assetId: string): Promise<AxiosResponse<{ analysisAssetId: string }>>;
}

export type FeatureFlags = {
  [key in FeatureFlagName]: boolean;
};

export interface FlisRecordResult {
  niin: string;
  nsn: string;
  itemName?: string;
}

interface SearchFlisRecordsResponse {
  flisRecords: FlisRecordResult[];
}

export type PartFinderFeedbackRating = 'up' | 'down';
export type PartFinderFeedbackTargetType = 'recommendation' | 'sourcing_opportunity';

export interface PartFinderFeedback {
  id: string;
  reportId: string;
  targetType: PartFinderFeedbackTargetType;
  targetId: string;
  rating: PartFinderFeedbackRating;
  note?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SetPartFinderFeedbackRequest {
  rating: PartFinderFeedbackRating;
  note?: string;
}

interface SetPartFinderFeedbackResponse {
  feedback: PartFinderFeedback;
}

export interface ListPartFinderFeedbackResponse {
  feedback: PartFinderFeedback[];
}

interface EnhancedTenantsType {
  getFeatureFlags(tenantId: string): Promise<AxiosResponse<FeatureFlags>>;
  searchFlisRecords(tenantId: string, q: string, limit?: number): Promise<AxiosResponse<SearchFlisRecordsResponse>>;
  listNsnCandidates(tenantId: string, partNumber: string): Promise<AxiosResponse<ListNsnCandidatesResponse>>;
  createPartFinderReport(
    tenantId: string,
    data: CreatePartFinderReportRequest
  ): Promise<AxiosResponse<CreatePartFinderReportResponse>>;
  createPartFinderReportFromImages(
    tenantId: string,
    data: PartImageSearchRequest
  ): Promise<AxiosResponse<PartImageIdentificationResponse>>;
  getPartImageIdentification(
    tenantId: string,
    identificationId: string
  ): Promise<AxiosResponse<PartImageIdentificationResponse>>;
  createReportFromPartImageCandidate(
    tenantId: string,
    identificationId: string,
    data: CreateReportFromPartImageCandidateRequest
  ): Promise<AxiosResponse<CreateReportFromPartImageCandidateResponse>>;
  listPartImageCharacteristicQuestions(
    tenantId: string,
    identificationId: string,
    itemType?: PartImageItemTypeOption,
    target?: PartImageNarrowTarget
  ): Promise<AxiosResponse<PartImageCharacteristicQuestionsResponse>>;
  narrowPartImageCandidates(
    tenantId: string,
    identificationId: string,
    data: NarrowPartImageCandidatesRequest
  ): Promise<AxiosResponse<NarrowPartImageCandidatesResponse>>;
  searchPartImageItemTypes(tenantId: string, search: string): Promise<AxiosResponse<PartImageItemTypesResponse>>;
  mergePartImageItems(
    tenantId: string,
    identificationId: string,
    data: MergePartImageItemsRequest
  ): Promise<AxiosResponse<PartImageIdentificationResponse>>;
  splitPartImageItem(
    tenantId: string,
    identificationId: string,
    itemIndex: number
  ): Promise<AxiosResponse<PartImageIdentificationResponse>>;
  cropPartImageItem(
    tenantId: string,
    identificationId: string,
    itemIndex: number,
    data: CropPartImageItemRequest
  ): Promise<AxiosResponse<PartImageIdentificationResponse>>;
  unmergePartImageItem(
    tenantId: string,
    identificationId: string,
    itemIndex: number
  ): Promise<AxiosResponse<PartImageIdentificationResponse>>;
  createBatchFromPartImageItems(
    tenantId: string,
    identificationId: string,
    data: CreateBatchFromPartImageItemsRequest
  ): Promise<AxiosResponse<CreateBatchFromPartImageItemsResponse>>;
  listPartFinderReports(
    tenantId: string,
    page?: number,
    perPage?: number,
    batchId?: string,
    topLevelOnly?: boolean,
    partNumberSearch?: string
  ): Promise<AxiosResponse<ListPartFinderReportsResponse>>;
  getPartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<GetPartFinderReportResponse>>;
  getPartFinderReportFailureDiagnostics(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<FailureDiagnosticsResponse>>;
  getPartFinderReportRelationships(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<GetPartFinderReportRelationshipsResponse>>;
  createPartFinderBatch(
    tenantId: string,
    data: CreatePartFinderBatchRequest
  ): Promise<AxiosResponse<CreatePartFinderBatchResponse>>;
  listPartFinderBatches(
    tenantId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<ListPartFinderBatchesResponse>>;
  getPartFinderBatch(tenantId: string, id: string): Promise<AxiosResponse<GetPartFinderBatchResponse>>;
  updatePartFinderBatchName(
    tenantId: string,
    id: string,
    data: UpdatePartFinderBatchNameRequest
  ): Promise<AxiosResponse<UpdatePartFinderBatchNameResponse>>;
  clearPartFinderBatchName(tenantId: string, id: string): Promise<AxiosResponse<UpdatePartFinderBatchNameResponse>>;
  cancelPartFinderBatch(tenantId: string, id: string): Promise<AxiosResponse<GetPartFinderBatchResponse>>;
  deletePartFinderBatch(tenantId: string, id: string): Promise<AxiosResponse<void>>;
  listPartFinderActivity(
    tenantId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<ListPartFinderActivityResponse>>;
  deletePartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<void>>;
  retryPartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<RetryPartFinderReportResponse>>;
  cancelPartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<CancelPartFinderReportResponse>>;
  updatePartFinderReportFinalRecommendation(
    tenantId: string,
    id: string,
    data: UpdatePartFinderReportRecommendationRequest
  ): Promise<AxiosResponse<UpdatePartFinderReportRecommendationResponse>>;
  clearPartFinderReportFinalRecommendation(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<UpdatePartFinderReportRecommendationResponse>>;
  updatePartFinderReportDescription(
    tenantId: string,
    id: string,
    data: UpdatePartFinderReportDescriptionRequest
  ): Promise<AxiosResponse<UpdatePartFinderReportDescriptionResponse>>;
  clearPartFinderReportDescription(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<UpdatePartFinderReportDescriptionResponse>>;
  listPartFinderFeedback(tenantId: string, id: string): Promise<AxiosResponse<ListPartFinderFeedbackResponse>>;
  setPartFinderFeedback(
    tenantId: string,
    id: string,
    targetId: string,
    data: SetPartFinderFeedbackRequest
  ): Promise<AxiosResponse<SetPartFinderFeedbackResponse>>;
  clearPartFinderFeedback(tenantId: string, id: string, targetId: string): Promise<AxiosResponse<void>>;
  getRootFilters(tenantId: string): Promise<AxiosResponse<GetFolderFiltersResponse>>;
  getFolderFilters(tenantId: string, folderId: string): Promise<AxiosResponse<GetFolderFiltersResponse>>;
  getRootMetadataFieldsWithData(tenantId: string): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>>;
  getFolderMetadataFieldsWithData(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>>;
  getRootHasSubfolderDescriptions(tenantId: string): Promise<AxiosResponse<HasSubfolderDescriptionsResponse>>;
  getFolderHasSubfolderDescriptions(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<HasSubfolderDescriptionsResponse>>;
  getRootLabelOptions(tenantId: string): Promise<AxiosResponse<FolderLabelOptionsResponse>>;
  getFolderLabelOptions(tenantId: string, folderId: string): Promise<AxiosResponse<FolderLabelOptionsResponse>>;
  getRootColumnVisibility(tenantId: string): Promise<AxiosResponse<FolderColumnVisibilityResponse>>;
  getFolderColumnVisibility(tenantId: string, folderId: string): Promise<AxiosResponse<FolderColumnVisibilityResponse>>;
  getReportGroupFilters(
    tenantId: string,
    reportId: string,
    groupId: string
  ): Promise<AxiosResponse<GetReportGroupFiltersResponse>>;
  getSystemHealth(tenantId: string, days?: number): Promise<AxiosResponse<SystemHealthResponse>>;
}
export class Api extends GeneratedApi<unknown> {
  declare assets: EnhancedAssetsType;
  declare tenants: EnhancedTenantsType;

  constructor(http: HttpClient) {
    super(http);
    Object.assign(this.assets, {
      getSupplementalAssets: this.getSupplementalAssets.bind(this),
      deleteSupplementalAssets: this.deleteSupplementalAssets.bind(this),
      putCriticalGeometry: this.putCriticalGeometry.bind(this),
      putManifestCriticalGeometry: this.putManifestCriticalGeometry.bind(this),
    });

    if (!this.tenants) {
      this.tenants = {} as EnhancedTenantsType;
    }
    Object.assign(this.tenants, {
      getFeatureFlags: this.getFeatureFlags.bind(this),
      getSystemHealth: this.getSystemHealth.bind(this),
      searchFlisRecords: this.searchFlisRecords.bind(this),
      listNsnCandidates: this.listNsnCandidates.bind(this),
      createPartFinderReport: this.createPartFinderReport.bind(this),
      createPartFinderReportFromImages: this.createPartFinderReportFromImages.bind(this),
      getPartImageIdentification: this.getPartImageIdentification.bind(this),
      createReportFromPartImageCandidate: this.createReportFromPartImageCandidate.bind(this),
      listPartImageCharacteristicQuestions: this.listPartImageCharacteristicQuestions.bind(this),
      narrowPartImageCandidates: this.narrowPartImageCandidates.bind(this),
      searchPartImageItemTypes: this.searchPartImageItemTypes.bind(this),
      mergePartImageItems: this.mergePartImageItems.bind(this),
      splitPartImageItem: this.splitPartImageItem.bind(this),
      cropPartImageItem: this.cropPartImageItem.bind(this),
      unmergePartImageItem: this.unmergePartImageItem.bind(this),
      createBatchFromPartImageItems: this.createBatchFromPartImageItems.bind(this),
      listPartFinderReports: this.listPartFinderReports.bind(this),
      getPartFinderReport: this.getPartFinderReport.bind(this),
      getPartFinderReportFailureDiagnostics: this.getPartFinderReportFailureDiagnostics.bind(this),
      getPartFinderReportRelationships: this.getPartFinderReportRelationships.bind(this),
      createPartFinderBatch: this.createPartFinderBatch.bind(this),
      listPartFinderBatches: this.listPartFinderBatches.bind(this),
      getPartFinderBatch: this.getPartFinderBatch.bind(this),
      updatePartFinderBatchName: this.updatePartFinderBatchName.bind(this),
      clearPartFinderBatchName: this.clearPartFinderBatchName.bind(this),
      cancelPartFinderBatch: this.cancelPartFinderBatch.bind(this),
      deletePartFinderBatch: this.deletePartFinderBatch.bind(this),
      listPartFinderActivity: this.listPartFinderActivity.bind(this),
      deletePartFinderReport: this.deletePartFinderReport.bind(this),
      retryPartFinderReport: this.retryPartFinderReport.bind(this),
      cancelPartFinderReport: this.cancelPartFinderReport.bind(this),
      updatePartFinderReportFinalRecommendation: this.updatePartFinderReportFinalRecommendation.bind(this),
      clearPartFinderReportFinalRecommendation: this.clearPartFinderReportFinalRecommendation.bind(this),
      updatePartFinderReportDescription: this.updatePartFinderReportDescription.bind(this),
      clearPartFinderReportDescription: this.clearPartFinderReportDescription.bind(this),
      listPartFinderFeedback: this.listPartFinderFeedback.bind(this),
      setPartFinderFeedback: this.setPartFinderFeedback.bind(this),
      clearPartFinderFeedback: this.clearPartFinderFeedback.bind(this),
      getRootFilters: this.getRootFilters.bind(this),
      getFolderFilters: this.getFolderFilters.bind(this),
      getRootMetadataFieldsWithData: this.getRootMetadataFieldsWithData.bind(this),
      getFolderMetadataFieldsWithData: this.getFolderMetadataFieldsWithData.bind(this),
      getRootHasSubfolderDescriptions: this.getRootHasSubfolderDescriptions.bind(this),
      getFolderHasSubfolderDescriptions: this.getFolderHasSubfolderDescriptions.bind(this),
      getRootLabelOptions: this.getRootLabelOptions.bind(this),
      getFolderLabelOptions: this.getFolderLabelOptions.bind(this),
      getRootColumnVisibility: this.getRootColumnVisibility.bind(this),
      getFolderColumnVisibility: this.getFolderColumnVisibility.bind(this),
      getReportGroupFilters: this.getReportGroupFilters.bind(this),
    });
  }

  private async searchFlisRecords(
    tenantId: string,
    q: string,
    limit?: number
  ): Promise<AxiosResponse<SearchFlisRecordsResponse>> {
    return this.http.request<SearchFlisRecordsResponse>({
      path: `/tenants/${tenantId}/flis-records/search`,
      method: 'GET',
      query: {
        q,
        ...(limit !== undefined && { limit }),
      },
      secure: true,
      format: 'json',
    });
  }

  private async getFeatureFlags(tenantId: string): Promise<AxiosResponse<FeatureFlags>> {
    return this.http.request<FeatureFlags>({
      path: `/tenants/${tenantId}/feature-flags`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getSystemHealth(tenantId: string, days?: number): Promise<AxiosResponse<SystemHealthResponse>> {
    return this.http.request<SystemHealthResponse>({
      path: `/tenants/${tenantId}/system-health`,
      method: 'GET',
      query: {
        ...(days !== undefined && { days }),
      },
      secure: true,
      format: 'json',
    });
  }

  private listNsnCandidates(tenantId: string, partNumber: string): Promise<AxiosResponse<ListNsnCandidatesResponse>> {
    return this.http.request<ListNsnCandidatesResponse>({
      path: `/tenants/${tenantId}/part-finder/nsn-candidates`,
      method: 'GET',
      query: { partNumber },
      secure: true,
      format: 'json',
    });
  }

  private async createPartFinderReport(
    tenantId: string,
    data: CreatePartFinderReportRequest
  ): Promise<AxiosResponse<CreatePartFinderReportResponse>> {
    return this.http.request<CreatePartFinderReportResponse>({
      path: `/tenants/${tenantId}/part-finder/reports`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async listPartFinderReports(
    tenantId: string,
    page?: number,
    perPage?: number,
    batchId?: string,
    topLevelOnly?: boolean,
    partNumberSearch?: string
  ): Promise<AxiosResponse<ListPartFinderReportsResponse>> {
    return this.http.request<ListPartFinderReportsResponse>({
      path: `/tenants/${tenantId}/part-finder/reports`,
      method: 'GET',
      query: {
        ...(page !== undefined && { page }),
        ...(perPage !== undefined && { perPage }),
        ...(batchId !== undefined && { batchId }),
        ...(topLevelOnly !== undefined && { topLevelOnly }),
        ...(partNumberSearch !== undefined && { partNumberSearch }),
      },
      secure: true,
      format: 'json',
    });
  }

  private createPartFinderReportFromImages(
    tenantId: string,
    data: PartImageSearchRequest
  ): Promise<AxiosResponse<PartImageIdentificationResponse>> {
    return this.http.request<PartImageIdentificationResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/from-images`,
      method: 'POST',
      body: Object.fromEntries(Object.entries(data).filter(([, value]) => value !== undefined)),
      type: ContentType.FormData,
      secure: true,
      format: 'json',
    });
  }

  private getPartImageIdentification(
    tenantId: string,
    identificationId: string
  ): Promise<AxiosResponse<PartImageIdentificationResponse>> {
    return this.http.request<PartImageIdentificationResponse>({
      path: `/tenants/${tenantId}/part-finder/image-identifications/${identificationId}`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private createReportFromPartImageCandidate(
    tenantId: string,
    identificationId: string,
    data: CreateReportFromPartImageCandidateRequest
  ): Promise<AxiosResponse<CreateReportFromPartImageCandidateResponse>> {
    return this.http.request<CreateReportFromPartImageCandidateResponse>({
      path: `/tenants/${tenantId}/part-finder/image-identifications/${identificationId}/reports`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private listPartImageCharacteristicQuestions(
    tenantId: string,
    identificationId: string,
    itemType?: PartImageItemTypeOption,
    target?: PartImageNarrowTarget
  ): Promise<AxiosResponse<PartImageCharacteristicQuestionsResponse>> {
    return this.http.request<PartImageCharacteristicQuestionsResponse>({
      path: `/tenants/${tenantId}/part-finder/image-identifications/${identificationId}/characteristic-questions`,
      method: 'GET',
      query: { ...itemType, ...target },
      secure: true,
      format: 'json',
    });
  }

  private searchPartImageItemTypes(
    tenantId: string,
    search: string
  ): Promise<AxiosResponse<PartImageItemTypesResponse>> {
    return this.http.request<PartImageItemTypesResponse>({
      path: `/tenants/${tenantId}/part-finder/item-types`,
      method: 'GET',
      query: { search },
      secure: true,
      format: 'json',
    });
  }

  private mergePartImageItems(
    tenantId: string,
    identificationId: string,
    data: MergePartImageItemsRequest
  ): Promise<AxiosResponse<PartImageIdentificationResponse>> {
    return this.http.request<PartImageIdentificationResponse>({
      path: `/tenants/${tenantId}/part-finder/image-identifications/${identificationId}/items/merge`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private splitPartImageItem(
    tenantId: string,
    identificationId: string,
    itemIndex: number
  ): Promise<AxiosResponse<PartImageIdentificationResponse>> {
    return this.itemAction(tenantId, identificationId, itemIndex, 'split');
  }

  private cropPartImageItem(
    tenantId: string,
    identificationId: string,
    itemIndex: number,
    data: CropPartImageItemRequest
  ): Promise<AxiosResponse<PartImageIdentificationResponse>> {
    return this.http.request<PartImageIdentificationResponse>({
      path: `/tenants/${tenantId}/part-finder/image-identifications/${identificationId}/items/${itemIndex}/crop`,
      method: 'PUT',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private unmergePartImageItem(
    tenantId: string,
    identificationId: string,
    itemIndex: number
  ): Promise<AxiosResponse<PartImageIdentificationResponse>> {
    return this.itemAction(tenantId, identificationId, itemIndex, 'unmerge');
  }

  private itemAction(
    tenantId: string,
    identificationId: string,
    itemIndex: number,
    action: 'split' | 'unmerge'
  ): Promise<AxiosResponse<PartImageIdentificationResponse>> {
    return this.http.request<PartImageIdentificationResponse>({
      path: `/tenants/${tenantId}/part-finder/image-identifications/${identificationId}/items/${itemIndex}/${action}`,
      method: 'POST',
      secure: true,
      format: 'json',
    });
  }

  private createBatchFromPartImageItems(
    tenantId: string,
    identificationId: string,
    data: CreateBatchFromPartImageItemsRequest
  ): Promise<AxiosResponse<CreateBatchFromPartImageItemsResponse>> {
    return this.http.request<CreateBatchFromPartImageItemsResponse>({
      path: `/tenants/${tenantId}/part-finder/image-identifications/${identificationId}/batches`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private narrowPartImageCandidates(
    tenantId: string,
    identificationId: string,
    data: NarrowPartImageCandidatesRequest
  ): Promise<AxiosResponse<NarrowPartImageCandidatesResponse>> {
    return this.http.request<NarrowPartImageCandidatesResponse>({
      path: `/tenants/${tenantId}/part-finder/image-identifications/${identificationId}/narrow`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async createPartFinderBatch(
    tenantId: string,
    data: CreatePartFinderBatchRequest
  ): Promise<AxiosResponse<CreatePartFinderBatchResponse>> {
    return this.http.request<CreatePartFinderBatchResponse>({
      path: `/tenants/${tenantId}/part-finder/batches`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async listPartFinderBatches(
    tenantId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<ListPartFinderBatchesResponse>> {
    return this.http.request<ListPartFinderBatchesResponse>({
      path: `/tenants/${tenantId}/part-finder/batches`,
      method: 'GET',
      query: {
        ...(page !== undefined && { page }),
        ...(perPage !== undefined && { perPage }),
      },
      secure: true,
      format: 'json',
    });
  }

  private async getPartFinderBatch(tenantId: string, id: string): Promise<AxiosResponse<GetPartFinderBatchResponse>> {
    return this.http.request<GetPartFinderBatchResponse>({
      path: `/tenants/${tenantId}/part-finder/batches/${id}`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async updatePartFinderBatchName(
    tenantId: string,
    id: string,
    data: UpdatePartFinderBatchNameRequest
  ): Promise<AxiosResponse<UpdatePartFinderBatchNameResponse>> {
    return this.http.request<UpdatePartFinderBatchNameResponse>({
      path: `/tenants/${tenantId}/part-finder/batches/${id}/name`,
      method: 'PATCH',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async clearPartFinderBatchName(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<UpdatePartFinderBatchNameResponse>> {
    return this.http.request<UpdatePartFinderBatchNameResponse>({
      path: `/tenants/${tenantId}/part-finder/batches/${id}/name`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async cancelPartFinderBatch(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<GetPartFinderBatchResponse>> {
    return this.http.request<GetPartFinderBatchResponse>({
      path: `/tenants/${tenantId}/part-finder/batches/${id}/cancel`,
      method: 'POST',
      secure: true,
      format: 'json',
    });
  }

  private async deletePartFinderBatch(tenantId: string, id: string): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/part-finder/batches/${id}`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async listPartFinderActivity(
    tenantId: string,
    page?: number,
    perPage?: number
  ): Promise<AxiosResponse<ListPartFinderActivityResponse>> {
    return this.http.request<ListPartFinderActivityResponse>({
      path: `/tenants/${tenantId}/part-finder/activity`,
      method: 'GET',
      query: {
        ...(page !== undefined && { page }),
        ...(perPage !== undefined && { perPage }),
      },
      secure: true,
      format: 'json',
    });
  }

  private async getPartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<GetPartFinderReportResponse>> {
    return this.http.request<GetPartFinderReportResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private getPartFinderReportRelationships(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<GetPartFinderReportRelationshipsResponse>> {
    return this.http.request<GetPartFinderReportRelationshipsResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/relationships`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getPartFinderReportFailureDiagnostics(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<FailureDiagnosticsResponse>> {
    return this.http.request<FailureDiagnosticsResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/failure-diagnostics`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async deletePartFinderReport(tenantId: string, id: string): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async retryPartFinderReport(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<RetryPartFinderReportResponse>> {
    return this.http.request<RetryPartFinderReportResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/retry`,
      method: 'POST',
      secure: true,
      format: 'json',
    });
  }

  private async cancelPartFinderReport(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<CancelPartFinderReportResponse>> {
    return this.http.request<CancelPartFinderReportResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/cancel`,
      method: 'POST',
      secure: true,
      format: 'json',
    });
  }

  private async updatePartFinderReportFinalRecommendation(
    tenantId: string,
    id: string,
    data: UpdatePartFinderReportRecommendationRequest
  ): Promise<AxiosResponse<UpdatePartFinderReportRecommendationResponse>> {
    return this.http.request<UpdatePartFinderReportRecommendationResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/final-recommendation`,
      method: 'PATCH',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async clearPartFinderReportFinalRecommendation(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<UpdatePartFinderReportRecommendationResponse>> {
    return this.http.request<UpdatePartFinderReportRecommendationResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/final-recommendation`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async listPartFinderFeedback(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<ListPartFinderFeedbackResponse>> {
    return this.http.request<ListPartFinderFeedbackResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/feedback`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async setPartFinderFeedback(
    tenantId: string,
    id: string,
    targetId: string,
    data: SetPartFinderFeedbackRequest
  ): Promise<AxiosResponse<SetPartFinderFeedbackResponse>> {
    return this.http.request<SetPartFinderFeedbackResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/feedback/${targetId}`,
      method: 'PUT',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async clearPartFinderFeedback(tenantId: string, id: string, targetId: string): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/feedback/${targetId}`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async updatePartFinderReportDescription(
    tenantId: string,
    id: string,
    data: UpdatePartFinderReportDescriptionRequest
  ): Promise<AxiosResponse<UpdatePartFinderReportDescriptionResponse>> {
    return this.http.request<UpdatePartFinderReportDescriptionResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/description`,
      method: 'PATCH',
      body: data,
      secure: true,
      format: 'json',
    });
  }

  private async clearPartFinderReportDescription(
    tenantId: string,
    id: string
  ): Promise<AxiosResponse<UpdatePartFinderReportDescriptionResponse>> {
    return this.http.request<UpdatePartFinderReportDescriptionResponse>({
      path: `/tenants/${tenantId}/part-finder/reports/${id}/description`,
      method: 'DELETE',
      secure: true,
      format: 'json',
    });
  }

  private async getSupplementalAssets(
    tenantId: string,
    assetId: string,
    page: number = 1,
    perPage: number = 20
  ): Promise<AxiosResponse<{ supplementalAssets: unknown[]; pageData: unknown }>> {
    return this.http.request<{ supplementalAssets: unknown[]; pageData: unknown }>({
      path: `/tenants/${tenantId}/assets/${assetId}/supplemental-assets`,
      method: 'GET',
      secure: true,
      format: 'json',
      query: { page, perPage },
    });
  }

  private async deleteSupplementalAssets(
    tenantId: string,
    assetId: string,
    data: { supplementalAssetIds: string[] }
  ): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/assets/${assetId}/supplemental-assets`,
      method: 'DELETE',
      secure: true,
      format: 'json',
      body: data,
    });
  }

  private async putCriticalGeometry(
    tenantId: string,
    assetId: string,
    data: { releaseUnitVector?: [number, number, number] }
  ): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/tenants/${tenantId}/assets/${assetId}/critical-geometry-analysis`,
      method: 'PATCH',
      secure: true,
      format: 'json',
      body: data,
    });
  }

  private async putManifestCriticalGeometry(
    tenantId: string,
    assetId: string
  ): Promise<AxiosResponse<{ analysisAssetId: string }>> {
    return this.http.request<{ analysisAssetId: string }>({
      path: `/tenants/${tenantId}/assets/${assetId}/manifest-critical-geometry`,
      method: 'PUT',
      secure: true,
      format: 'json',
    });
  }

  private async getRootFilters(tenantId: string): Promise<AxiosResponse<GetFolderFiltersResponse>> {
    return this.http.request<GetFolderFiltersResponse>({
      path: `/tenants/${tenantId}/folders/root/filter-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderFilters(tenantId: string, folderId: string): Promise<AxiosResponse<GetFolderFiltersResponse>> {
    return this.http.request<GetFolderFiltersResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/filter-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getRootMetadataFieldsWithData(
    tenantId: string
  ): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>> {
    return this.http.request<GetMetadataFieldsWithDataResponse>({
      path: `/tenants/${tenantId}/folders/root/metadata-fields-with-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderMetadataFieldsWithData(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<GetMetadataFieldsWithDataResponse>> {
    return this.http.request<GetMetadataFieldsWithDataResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/metadata-fields-with-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getRootHasSubfolderDescriptions(
    tenantId: string
  ): Promise<AxiosResponse<HasSubfolderDescriptionsResponse>> {
    return this.http.request<HasSubfolderDescriptionsResponse>({
      path: `/tenants/${tenantId}/folders/root/has-subfolder-descriptions`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderHasSubfolderDescriptions(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<HasSubfolderDescriptionsResponse>> {
    return this.http.request<HasSubfolderDescriptionsResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/has-subfolder-descriptions`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getRootLabelOptions(tenantId: string): Promise<AxiosResponse<FolderLabelOptionsResponse>> {
    return this.http.request<FolderLabelOptionsResponse>({
      path: `/tenants/${tenantId}/folders/root/label-options`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderLabelOptions(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<FolderLabelOptionsResponse>> {
    return this.http.request<FolderLabelOptionsResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/label-options`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getRootColumnVisibility(tenantId: string): Promise<AxiosResponse<FolderColumnVisibilityResponse>> {
    return this.http.request<FolderColumnVisibilityResponse>({
      path: `/tenants/${tenantId}/folders/root/column-visibility`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getFolderColumnVisibility(
    tenantId: string,
    folderId: string
  ): Promise<AxiosResponse<FolderColumnVisibilityResponse>> {
    return this.http.request<FolderColumnVisibilityResponse>({
      path: `/tenants/${tenantId}/folders/${folderId}/column-visibility`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  private async getReportGroupFilters(
    tenantId: string,
    reportId: string,
    groupId: string
  ): Promise<AxiosResponse<GetReportGroupFiltersResponse>> {
    return this.http.request<GetReportGroupFiltersResponse>({
      path: `/tenants/${tenantId}/reports/${reportId}/groups/${groupId}/filter-data`,
      method: 'GET',
      secure: true,
      format: 'json',
    });
  }

  public async recordUsageEvent(data: {
    name: string;
    url: string;
    title?: string;
    referrer?: string;
    data?: Record<string, unknown>;
  }): Promise<AxiosResponse<void>> {
    return this.http.request<void>({
      path: `/usage/record`,
      method: 'POST',
      body: data,
      secure: true,
      format: 'json',
    });
  }
}
